import {defineStore} from 'pinia'

import axios from "@axios"
import {reportError} from "@/plugins/misc"
import localforage from "localforage"

const baseUrl = `/user/`

var listeningMyUser = false
let followQueue = Promise.resolve()

export const useUsersStore = defineStore({
  id: 'users',
  state: () => ({
    users: [], //JSON.parse(localStorage.getItem('users')),
    adminEditingUser: null,
    adminEditingUserSubscription: null,
    user: {},
    myUser: JSON.parse(localStorage.getItem('userData')),
    isEditingMyUser: false,
    // RM-430: a field Edit Profile opens on (Profile → RM via text → "Add mobile number")
    editMyUserFocus: null,
    isEditingSecondaryMember: false,

    // RM-150: personal admins management UI state
    isEditingPersonalAdmins: false,
    personalAdmins: [],
    personalAdminsLoading: false,

    // Set by /[profileId]/personal-admin so the login dialog can pre-fill
    // the locked email + carry the invitation token through to registration.
    personalAdminInvitation: null,
    canActForMembers: [],
    canActForLoading: false,

    // Restaurant group(s) the current user is associated with, derived from the
    // restaurants they administer (see fetchMyRestaurantGroups). Informational only.
    myRestaurantGroups: [],

    // RM-159: unreverted profile edits a Tier 2 spouse made on this account.
    spouseEdits: [],
    spouseEditsLoading: false,

    // RM-150: when set, the globally-mounted ChooseMemberForRequestDialog
    // opens with this context (restaurant/market/reservationType). The dialog
    // clears it on confirm/cancel.
    pendingReservationContext: null,
    isShowingCodeConfirmationDialog: false,
    isShowingVerificationDialog: false,
    isShowingMemberCommitmentDialog: false,

    // 'gate' = required acceptance flow (I Agree / Delete Account)
    // 'view' = read-only, opened from the request checkbox link
    memberCommitmentDialogMode: 'gate',
    memberCommitmentDialogSource: 'existing_member_gate',
    isShowingDeleteAccountDialog: false,
    isShowingGiftTrialDialog: false,
    isShowingPreferredRequestInfoDialog: false,
    isShowingPreferredTabsDialog: false,

    // Global For Restaurants modal (RestaurantsInfoDialog at its Preferred
    // Benefits tab) — opened from card Preferred seals + the Preferred tab
    // descriptor; mounted once in App.vue
    isShowingRestaurantsInfoDialog: false,
    isShowingMembersInfoDialog: false,
    membersInfoDialogTab: 'how-rm-works',
    loginDialogMode: false,
    loginDialogVerificationType: null,
    isShowingLoadingOverlay: false,
    isInitialized: false,
    fetching: false,
    saving: false,
    appVersion: localStorage.getItem('rm-version') || '0.0.0',
    activeServiceWorkerVersion: null,
    isRunningServiceWorker: (navigator?.serviceWorker && navigator.serviceWorker?.controller),
    isRunningInIOSAppStore: false,
    isRunningInStandAlone: false,
    focusListener: null,
    showingMainSearch: false,
    memberSearchResults: [],
    suggestions: [],
    suggestionsLoading: false,
    suggestionsDismissed: JSON.parse(localStorage.getItem('suggestionsDismissed') || '[]'),
  }),
  getters: {
    // The linked spouse/secondary account's user id, or null. Spouse pairs are
    // permanently connected (enforced server-side), so the UI uses this to hide
    // the "remove connection" control for the spouse.
    mySpouseId: state => (state.myUser?.spouseUserId ? String(state.myUser.spouseUserId) : null),
  },
  actions: {
    async loadInitialData() {
      if (this.isInitialized) {
        return  // If already initialized, do nothing
      }

      this.updateEnvironment()
      this.isInitialized = true

      // Always fetch fresh user data if logged in to avoid stale localStorage data
      if (localStorage.getItem('jwt')) {
        await this.getMyUser()
      }
    },
    async activateMainSearch() {
      this.showingMainSearch = true
      await nextTick()
    },
    async closeMainSearch() {
      this.showingMainSearch = false
    },
    async showVerificationDialog() {
      this.isShowingVerificationDialog = true
    },
    async hideVerificationDialog() {
      this.isShowingVerificationDialog = false
    },

    async setAppVersion(version) {
      this.appVersion = version
      localStorage.setItem('rm-version', version)
    },

    checkAppPlatformCookie() {
      const cookies = document.cookie.split('; ')
      const appPlatformCookie = cookies.find(cookie => cookie.startsWith('app-platform='))
      if (appPlatformCookie) {
        const value = appPlatformCookie.split('=')[1]
        
        return value === 'iOS App Store'
      }
      
      return false
    },
    updateEnvironment() {
      this.isRunningInStandAlone =  window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches
      this.isRunningInIOSAppStore = this.checkAppPlatformCookie()

      //below line for testing purposes
      //this.isRunningInIOSAppStore = false;
    },

    async showCodeConfirmationDialog() {
      this.isShowingCodeConfirmationDialog = true
    },
    async hideCodeConfirmationDialog() {
      this.isShowingCodeConfirmationDialog = false
    },
    async showMemberCommitmentDialog({ mode = 'gate', source = 'existing_member_gate' } = {}) {
      this.memberCommitmentDialogMode = mode
      this.memberCommitmentDialogSource = source
      this.isShowingMemberCommitmentDialog = true
    },
    async hideMemberCommitmentDialog() {
      this.isShowingMemberCommitmentDialog = false
    },
    async acceptMemberCommitment(source) {
      try {
        const res = await axios.post(
          `${baseUrl}me/member-commitment/accept`,
          { source },
          { params: {} },
        )
        if (this.myUser) {
          this.myUser.memberCommitment = res.data.memberCommitment
          localStorage.setItem('userData', JSON.stringify(this.myUser))
        }
        this.isShowingMemberCommitmentDialog = false
        
        return true
      } catch (error) {
        reportError('users.store.js:acceptMemberCommitment', error.message, null, null, null, error)
        
        return false
      }
    },
    async showDeleteAccountDialog() {
      this.isShowingDeleteAccountDialog = true
    },
    async hideDeleteAccountDialog() {
      this.isShowingDeleteAccountDialog = false
    },
    async showSecondaryMemberDialog() {
      this.isEditingSecondaryMember = true
    },
    async hideSecondaryMemberDialog() {
      this.isEditingSecondaryMember = false
    },
    async showPersonalAdminsDialog() {
      this.isEditingPersonalAdmins = true
      await this.fetchPersonalAdmins()
    },
    async hidePersonalAdminsDialog() {
      this.isEditingPersonalAdmins = false
    },
    async fetchPersonalAdmins() {
      this.personalAdminsLoading = true
      try {
        const { data } = await axios.get('/user/me/personal-admins')
        this.personalAdmins = data.personalAdmins || []
      } catch (err) {
        reportError('users.store.js:fetchPersonalAdmins', err?.message, null, null, null, err)
      } finally {
        this.personalAdminsLoading = false
      }
    },
    async invitePersonalAdmin(email) {
      const { data } = await axios.post('/user/me/personal-admins', { email })
      this.personalAdmins = data.personalAdmins || []
      
      return data
    },
    async revokePersonalAdmin(email) {
      const { data } = await axios.delete(`/user/me/personal-admins/${encodeURIComponent(email)}`)
      this.personalAdmins = data.personalAdmins || []
      
      return data
    },
    async fetchCanActForMembers() {
      this.canActForLoading = true
      try {
        const { data } = await axios.get('/user/me/can-act-for')
        this.canActForMembers = data.members || []
        
        return data
      } catch (err) {
        reportError('users.store.js:fetchCanActForMembers', err?.message, null, null, null, err)
        
        return { members: [], isPlatformAdmin: false }
      } finally {
        this.canActForLoading = false
      }
    },

    // Restaurant group(s) the current user is associated with, derived server-side
    // from the restaurants they administer. Informational only (no management
    // rights). Returns an array — a restaurant admin can span multiple groups.
    async fetchMyRestaurantGroups() {
      try {
        const { data } = await axios.get('/user/me/restaurant-group-scope')
        this.myRestaurantGroups = data.restaurantGroups || []

        return data
      } catch (err) {
        reportError('users.store.js:fetchMyRestaurantGroups', err?.message, null, null, null, err)

        return { restaurantGroups: [] }
      }
    },

    // RM-159: load unreverted profile edits made by a Tier 2 spouse so the
    // account holder can review and revert them on their own profile.
    async fetchSpouseEdits() {
      this.spouseEditsLoading = true
      try {
        const { data } = await axios.get('/user/me/spouse-edits')
        this.spouseEdits = data.edits || []

        return data
      } catch (err) {
        reportError('users.store.js:fetchSpouseEdits', err?.message, null, null, null, err)

        return { edits: [] }
      } finally {
        this.spouseEditsLoading = false
      }
    },
    async revertSpouseEdit(logId) {
      await axios.post(`/user/me/spouse-edits/${logId}/revert`)
      this.spouseEdits = this.spouseEdits.filter(e => e._id !== logId)
      await this.getMyUser()
    },

    /**
     * RM-150: single entry point for "start a new reservation" used by every
     * trigger in the app (search, restaurant page, nav, etc.). Decides whether
     * to open the ChooseMemberForRequestDialog or create the draft directly.
     */
    async startNewReservation({ restaurant = null, market = null, reservationType = 'general' } = {}) {
      const { useReservationsStore } = await import('@/stores/reservations.store')
      const { useUserGroupsStore } = await import('@/stores/userGroups.store')
      const { needsHostSelection } = await import('@/utils/newReservationGate')
      const reservationsStore = useReservationsStore()
      const userGroupsStore = useUserGroupsStore()

      // Dummy / unauthenticated users go straight to the standard draft path.
      if (!this.myUser?.email || this.myUser.userLevel === 'dummy') {
        await reservationsStore.newDraftReservation(null, null, restaurant, market, { reservationType })
        
        return
      }

      // Ensure group + can-act-for data is loaded before deciding.
      if (this.myUser.userLevel !== 'admin' && (!userGroupsStore.userGroups || userGroupsStore.userGroups.length === 0)) {
        try { await userGroupsStore.getMyUserGroups() } catch (_) { /* fall through */ }
      }
      // Fetch can-act-for for everyone (incl. platform admins): admins now get
      // their personal Platinum spouse back from this endpoint (RM-159), so the
      // "Submit for <spouse>" host option can appear on their own account too.
      if (this.canActForMembers.length === 0) {
        try { await this.fetchCanActForMembers() } catch (_) { /* fall through */ }
      }

      const shouldPick = needsHostSelection({
        myUser: this.myUser,
        canActForMembers: this.canActForMembers,
      })
      if (shouldPick) {
        this.pendingReservationContext = { restaurant, market, reservationType }
      } else {
        await reservationsStore.newDraftReservation(null, null, restaurant, market, { reservationType })
      }
    },
    async showLoadingOverlay() {
      console.log("Showing loading overlay")
      this.isShowingLoadingOverlay = true
    },
    async hideLoadingOverlay() {
      console.log("Hiding loading overlay")
      this.isShowingLoadingOverlay = false
    },

    /**
         * @todo Refactor user data updating logic to the back-end
         * Currently, the `fetchUserById` function updates the user data and their 
         * relationships (e.g. `membersWhoAreFollowing` and `membersFollowed`) on the
         * client-side. This approach has several drawbacks:
         * 
         * - Security risk: Allowing clients to manipulate the data in unintended ways.
         * - Performance issues: Updating relationships on the client can be slow and inefficient for large datasets.
         * - Inconsistent data: Multiple clients updating the relationships independently may lead to inconsistent data.
         * - Network overhead: Sending the entire list of users and their relationships to the client for each update can be inefficient.
         */
    async fetchUserById(userId) {
      //this.myUser = { loading: true };
      if(!this.fetching) {
        try {
          let res = await axios.get(`${baseUrl}${userId}`, {
            params: {
            }
          })

          if (res.data) {
            let theUser = this.users.find(({_id}) => _id === userId)
            if(theUser){
              Object.assign(theUser, res.data)
            } else{
              this.users.push(res.data)
            }

            //for each theUser or this.users[this.users.length-1].membersWhoAreFollowing add the user to this.users
            if(res.data.membersWhoAreFollowing){
              for (let i = 0; i < res.data.membersWhoAreFollowing.length; i++) {
                let theUser = this.users.find(({_id}) => _id === res.data.membersWhoAreFollowing[i]._id)
                if(theUser){
                  Object.assign(theUser, res.data.membersWhoAreFollowing[i])
                } else{
                  this.users.push(res.data.membersWhoAreFollowing[i])
                }
              }
            }

            ///same as above except for membersFollowed
            if(res.data.membersFollowed){
              for (let i = 0; i < res.data.membersFollowed.length; i++) {
                let theUser = this.users.find(({_id}) => _id === res.data.membersFollowed[i]._id)
                if(theUser){
                  Object.assign(theUser, res.data.membersFollowed[i])
                } else{
                  this.users.push(res.data.membersFollowed[i])
                }
              }
            }

            return theUser || this.users[this.users.length-1]
          }
        } catch (error) {
          console.error(error)
          reportError("users.store.js:fetchUserById", error.message, null, null, null, error)
        }
      }
    },
    async getUserByProfileId(profileId) {
      try {
        let res = await axios.get(`${baseUrl}`, {
          params: {
            profileId: profileId
          }
        })

        if (res.data?.[0]) {
          let theUser = this.users.find(({id}) => id === res.data[0].id)
          if(!theUser){
            this.users.push(res.data[0])
          }
          else{
            Object.assign(theUser, res.data[0])
          }
        }
      } catch (error) {
        console.error(error)
        reportError("users.store.js:getUserByProfileId", error.message, null, null, null, error)
      }
    },

    async getMyUser() {
      console.log('[STORE] getMyUser called')
      try {
        console.log('[STORE] Fetching user data from API...')
        let res = await axios.get(`${baseUrl}me`, {
          params: {
          }
        })
        console.log('[STORE] API response received:', res.status, res.statusText)
        if (!res.data) {
          console.log("[STORE] No data, logout user!")
          localStorage.removeItem('userData')
          localStorage.removeItem('accessToken')
          localStorage.removeItem('jwt')
          location.href="/auth/logout?loc=5"
        } else {
          console.log('[STORE] User data received, updating store and localStorage')
          this.myUser = res.data
          localStorage.setItem('userData', JSON.stringify(this.myUser))

          // Init favorite notes in restaurants store
          if (res.data.favoriteNotes) {
            const { useRestaurantsStore } = await import('@/stores/restaurants.store')
            const restaurantsStore = useRestaurantsStore()
            restaurantsStore.initFavoriteNotes(res.data.favoriteNotes)
          }

          console.log('[STORE] getMyUser complete. User:', this.myUser.email)
        }
      } catch (error) {
        console.error('[STORE] getMyUser error:', error)
        reportError("users.store.js:getMyUser", error.message, null,null,null, error)
      }
    },
    async getMyFavoritesData() {
      try {
        const res = await axios.get(`${baseUrl}me/favorites-data`, {
          params: {},
        })
        if (res.data && this.myUser) {
          this.myUser.favoritedRestaurants = res.data.favoritedRestaurants
          this.myUser.favoritedRestaurantsBookingCount = res.data.favoritedRestaurantsBookingCount
          this.myUser.favoritedRestaurantsLastBookingDate = res.data.favoritedRestaurantsLastBookingDate
          this.myUser.favoritedRestaurantsLastSubmittedDate = res.data.favoritedRestaurantsLastSubmittedDate
          // RM-296: when each favorite was added, so "Recent" puts a just-added favorite on top
          this.myUser.favoritedAt = res.data.favoritedAt || {}
          // When they last asked for each — a request through RM or a text RM drafted
          this.myUser.favoritedLastRequestedAt = res.data.favoritedLastRequestedAt || {}
        }
      } catch (error) {
        reportError("users.store.js:getMyFavoritesData", error.message, null, null, null, error)
      }
    },
    async setupMyUserListeners() {
      if(this.focusListener){
        await this.cancelMyUserListeners()
      }
      this.focusListener = () => {
        this.getMyUser()
      }

      window.addEventListener("focus", this.focusListener)
    },

    async cancelMyUserListeners() {
      console.log("Canceling", this.focusListener)
      window.removeEventListener("focus", this.focusListener)
      this.focusListener = null
    },

    async editMyUser(focus = null) {
      await this.cancelMyUserListeners()
      this.editMyUserFocus = focus
      this.isEditingMyUser = true
    },

    async cancelEditUseMyUser() {
      this.isEditingMyUser = false
      await this.getMyUser()
      await this.setupMyUserListeners()
    },
    async cancelAdminEditUserSubscription() {
      this.adminEditingUserSubscription = null
    },


    async updateMyUser() {
      //this.myUser = { loading: true };
      try {
        let res = await axios.put(`${baseUrl}${this.myUser._id}`, this.myUser, {
          params: {
          }
        })
        this.myUser = res.data

        // let theUser = this.users.find(({id}) => id === this.myUser._id)
        // Object.assign(theUser, res.data)
        this.isEditingMyUser = false
        await this.setupMyUserListeners()
        localStorage.setItem('userData', JSON.stringify(this.myUser))
      } catch (error) {
        console.log("ERROR 5dd63", error)

        //reportError("reservations.store.js:updateMyUser", error.message, null,null,null, error)

        // const {errors: formErrors} = error.response.data
        return error.response.data
      }
    },

    async getAllUsers() {
      this.fetching = true
      try {
        let res = await axios.get(baseUrl, {
          params: {
          }
        })

        // Support both old array format and new paginated format
        if (res.data?.users) {
          this.users = res.data.users
        } else {
          this.users = res.data
        }

        this.fetching = false
      } catch (error) {
        console.error(error)
        reportError("users.store.js:getAllUsers", error.message, null,null,null, error)
      }
    },

    async getAdminUsers(params = {}) {
      this.fetching = true
      try {
        let res = await axios.get(baseUrl, {
          params: {
            ...params
          }
        })

        const { users: pageUsers, totalCount, cohortStats } = res.data
        this.users = pageUsers
        this.fetching = false

        return { users: pageUsers, totalCount, cohortStats }
      } catch (error) {
        console.error(error)
        this.fetching = false
        reportError("users.store.js:getAdminUsers", error.message, null, null, null, error)
        
        return { users: [], totalCount: 0 }
      }
    },

    async getSearchedUsers(searchTerm) {
      this.fetching = true
      try {
        let res = await axios.get(baseUrl, {
          params: {
            searchTerm: searchTerm
          }
        })

        const data = res.data?.users || res.data

        // Merge the new data with the existing data
        this.users = [...this.users, ...data]

        // Remove duplicates if necessary (based on some unique property, e.g., 'id')
        this.users = [...new Map(this.users.map(item => [item._id, item])).values()]

        this.fetching = false
      } catch (error) {
        console.error(error)
        reportError("users.store.js:getAllUsers", error.message, null,null,null, error)
      }
    },

    async getNewUsers() {
      this.fetching = true
      try {
        let res = await axios.get(baseUrl, {
          params: {
            limit: 20
          }
        })

        const data = res.data?.users || res.data

        // Merge the new data with the existing data
        this.users = [...this.users, ...data]

        // Remove duplicates if necessary (based on some unique property, e.g., 'id')
        this.users = [...new Map(this.users.map(item => [item._id, item])).values()]

        this.fetching = false
      } catch (error) {
        console.error(error)
        reportError("users.store.js:getAllUsers", error.message, null,null,null, error)
      }
    },


    async adminEditUser(idToEdit) {
      //be sure to get the full user so we have all the subscription info etc
      await this.fetchUserById(idToEdit)

      //make a copy of the user to edit
      this.adminEditingUser = Object.assign({}, this.users.find(({id}) => id === idToEdit))
    },

    async adminEditUserSubscription(idToEdit) {
      //fetch the full user so we have all the subscription info
      await this.fetchUserById(idToEdit)

      //make a copy of the user to edit
      this.adminEditingUserSubscription = Object.assign({}, this.users.find(({id}) => id === idToEdit))
    },
    async saveAdminEditingUserSubscription() {
      console.log("Saving")
      try {
        // Update user's subscription, just update do not overwrite.
        // The response for this is the full admin user, which is why we see incorrect subscriptions in the front-end.
        await axios.put(`${baseUrl}${this.adminEditingUserSubscription.id}`, this.adminEditingUserSubscription, {
          params: {
          }
        })

        var updateIndex = this.users.map(item => item.id).indexOf(this.adminEditingUserSubscription.id)
        this.users[updateIndex] = this.adminEditingUserSubscription

        await this.fetchUserById(this.adminEditingUserSubscription.id)

        console.log(`Subscription saved!`)
      } catch (error) {
        console.error(error)
        reportError("users.store.js:saveAdminEditingUserSubscription", error.message, null,null,null, error)
      }

    },
    // Edit one membership on a member's own record. The term, status and plan
    // details are recomputed server-side (lib/subscriptions) so an edited
    // membership matches one granted the same way — don't recompute here.
    // Returns an error string on failure, null on success.
    async adminEditMembership(userId, subscriptionId, changes) {
      try {
        const response = await axios.put(`${baseUrl}${userId}/subscriptions/${subscriptionId}`, changes)
        this.applyAdminMembershipResult(userId, response.data.subscriptions)

        return null
      } catch (error) {
        const message = error.response?.data?.error || error.message
        reportError("users.store.js:adminEditMembership", message, null, null, null, error)

        return message
      }
    },

    // Remove a membership outright. The server deletes it rather than marking it
    // Canceled — a canceled membership still counts as current coverage until
    // its paidThroughDate, so cancelling would not take back the access.
    async adminRemoveMembership(userId, subscriptionId) {
      try {
        const response = await axios.delete(`${baseUrl}${userId}/subscriptions/${subscriptionId}`)
        this.applyAdminMembershipResult(userId, response.data.subscriptions)

        return null
      } catch (error) {
        const message = error.response?.data?.error || error.message
        reportError("users.store.js:adminRemoveMembership", message, null, null, null, error)

        return message
      }
    },

    // Re-read the member so every derived field the UI shows (isPlatinum,
    // membershipType, platinumSubscriptionsByState) reflects the change.
    async applyAdminMembershipResult(userId, updatedSubscriptions) {
      if (this.adminEditingUserSubscription?.id === userId) {
        this.adminEditingUserSubscription.subscriptions = updatedSubscriptions
      }
      await this.fetchUserById(userId)

      const refreshed = this.users.find(({ id }) => id === userId)
      if (refreshed && this.adminEditingUserSubscription?.id === userId) {
        this.adminEditingUserSubscription = Object.assign({}, refreshed)
      }
    },

    async cancelAdminEditUser() {
      // if (this.adminEditingUser.isNew) {
      //     await this.adminDeleteUser(this.adminEditingUser.id)
      // }
      this.adminEditingUser = null

      //this.getAllUsers()
    },

    async saveAdminEditingUser() {
      let res
      try {
        let isNewUser = this.adminEditingUser.isNew
        if (isNewUser) {
          res = await axios.post(`${baseUrl}`, this.adminEditingUser, {
            params: {
            }
          })

          //this.adminEditingUser = res.data
        } else {
          res = await axios.put(`${baseUrl}${this.adminEditingUser.id}`, this.adminEditingUser, {
            params: {
            }
          })
        }


        //Object.assign(this.adminEditingUser, res.data)
        if (isNewUser) {
          this.users.push(res.data)
        } else {
          var updateIndex = this.users.map(item => item.id).indexOf(this.adminEditingUser.id)
          Object.assign(this.users[updateIndex], this.adminEditingUser)
        }
        this.adminEditingUser = null

        //console.log("Setting myReservations localStorage to ", this.users)
        // await localforage.setItem('users', JSON.stringify(this.users));
      } catch (error) {
        alert("Error saving user, perhaps that email is already in use -- reloading page")

        //reload page
        console.error(error)
        reportError("users.store.js:saveAdminEditingUser", error.message, null,null,null, error)
        location.reload()
      }
      
      return res.data.id
    },

    // Dismiss a member's Platinum upgrade request without granting a
    // membership — removes them from the admin Pending tab.
    async adminClearPlatinumUpgradeRequest(userId) {
      try {
        const res = await axios.post(`${baseUrl}${userId}/clear-platinum-upgrade-request`)

        const userIndex = this.users.findIndex(u => u.id === userId || u._id === userId)
        if (userIndex !== -1) {
          this.users[userIndex].pendingPlatinumApproval = res.data.pendingPlatinumApproval
        }

        return res.data
      } catch (error) {
        console.error(error)
        reportError("users.store.js:adminClearPlatinumUpgradeRequest", error.message, null, null, null, error)
      }
    },

    async adminToggleApproval(userId) {
      try {
        let res = await axios.post(`${baseUrl}${userId}/toggle-approval`, {}, {
          params: {
          }
        })

        // Update the user in the local array
        const userIndex = this.users.findIndex(u => u.id === userId || u._id === userId)
        if (userIndex !== -1) {
          this.users[userIndex].accountApproved = res.data.accountApproved
        }

        return res.data
      } catch (error) {
        console.error(error)
        reportError("users.store.js:adminToggleApproval", error.message, null, null, null, error)
      }
    },

    async adminDeleteUser(idToDelete) {

      try {
        let res = await axios.delete(`${baseUrl}${idToDelete}`, {
          params: {
          }
        })

        //remove matching item from array
        var removeIndex = this.users.map(item => item.id).indexOf(idToDelete)
        ~removeIndex && this.users.splice(removeIndex, 1)

        if (this.adminEditingUser?.id == idToDelete)
          this.adminEditingUser = null
      } catch (error) {
        console.error(error)
        reportError("users.store.js:adminDeleteUser", error.message, null,null,null, error)
      }

    },

    async adminNewUser() {
      try {
        this.adminEditingUser = {
          "firstName": "",
          "lastName": "",
          "company": "",
          "title": "",
          "email": "",
          "memberSince": null,
          "membershipSource": null,
          "secondaryAccountEmail": null,
          "platinum": false,
        }
        this.adminEditingUser.isNew = true
        this.adminEditingUser.email = ""
      } catch (error) {
        console.error(error)
        reportError("users.store.js:adminNewUser", error.message, null,null,null, error)
      }
    },

    async loginNewDummyUser() {
      try {
        let res = await axios.post(`/auth/logindummy`, {email:"dummy", password:"dummy"}, {
          params: {}
        })
        await this.getMyUser()

      } catch (error) {
        console.error(error)
        reportError("users.store.js:loginNewDummyUser", error.message, null,null,null, error)
      }
    },

    async searchMembers(textSearch) {
      try {
        const params = { }
        if(textSearch)  params.textSearch = textSearch
        const { data } = await axios.get(baseUrl, { params })

        //make copy of data
        let newResults = JSON.parse(JSON.stringify(data))

        //add fullName field based on firstName and lastName
        newResults.forEach(result => {
          result.fullName = `${result.firstName} ${result.lastName}`
        })

        this.memberSearchResults = newResults

        //push to this.users if not already in there
        newResults.forEach(result => {
          let index = this.users.findIndex(({_id}) => _id === result._id)
          if(index === -1){
            this.users.push(result)
          }
        })

      } catch (error) {
        console.log('err', error)
        reportError("restaurant.store.js:searchMembers", error.message, null,null,null, error)
      }
    },

    async getSuggestions() {
      this.suggestionsLoading = true
      try {
        const { data } = await axios.get(baseUrl, { params: { suggestions: true } })

        let newResults = JSON.parse(JSON.stringify(data))

        // Filter out dismissed suggestions
        newResults = newResults.filter(r => !this.suggestionsDismissed.includes(r._id))

        this.suggestions = newResults

        // Merge into this.users
        newResults.forEach(result => {
          let index = this.users.findIndex(({_id}) => _id === result._id)
          if (index === -1) {
            this.users.push(result)
          } else {
            Object.assign(this.users[index], result)
          }
        })
      } catch (error) {
        console.error('Error fetching suggestions:', error)
        reportError("users.store.js:getSuggestions", error.message, null, null, null, error)
      } finally {
        this.suggestionsLoading = false
      }
    },

    dismissSuggestion(userId) {
      this.suggestionsDismissed.push(userId)
      localStorage.setItem('suggestionsDismissed', JSON.stringify(this.suggestionsDismissed))
      this.suggestions = this.suggestions.filter(s => s._id !== userId)
    },

    async refreshNetwork() {
      await Promise.all([
        this.getFollowers(),
        this.getFollowing(),
        this.getFollowingPending(),
      ])
    },

    async getFollowing() {
      try {
        const params = {following: true }
        const { data } = await axios.get(baseUrl, { params })

        //make copy of data
        let users = JSON.parse(JSON.stringify(data))

        //replace any this.users with new users by _id
        users.forEach(user => {
          let index = this.users.findIndex(({_id}) => _id === user._id)
          if(index !== -1){
            Object.assign(this.users[index], user)
          } else{
            this.users.push(user)
          }
        })
      } catch (error) {
        console.log('err', error)
        reportError("restaurant.store.js:getFollowing", error.message, null,null,null, error)
      }
    },

    async getFollowingPending() {
      try {
        const params = {followingPending: true }
        const { data } = await axios.get(baseUrl, { params })

        //make copy of data
        let users = JSON.parse(JSON.stringify(data))

        //replace any this.users with new users by _id
        users.forEach(user => {
          let index = this.users.findIndex(({_id}) => _id === user._id)
          if(index !== -1){
            Object.assign(this.users[index], user)
          } else{
            this.users.push(user)
          }
        })
      } catch (error) {
        console.log('err', error)
        reportError("restaurant.store.js:getFollowing", error.message, null,null,null, error)
      }
    },

    async getFollowers() {
      try {
        const params = {followers: true }
        const { data } = await axios.get(baseUrl, { params })

        //make copy of data
        let users = JSON.parse(JSON.stringify(data))

        //replace any this.users with new users by _id
        users.forEach(user => {
          let index = this.users.findIndex(({_id}) => _id === user._id)
          if(index !== -1){
            Object.assign(this.users[index], user)
          } else{
            this.users.push(user)
          }
        })

        // await localforage.setItem('users', JSON.stringify(this.users));
      } catch (error) {
        console.log('err', error)
        reportError("restaurant.store.js:getFollowers", error.message, null,null,null, error)
      }
    },
    async follow(memberId) {

      //find this.user with _id of memberId, then set its followedByMyUser = false;
      let user = this.users.find(({_id}) => _id === memberId)

      if (user) {
        user.followedByMyUserPending = true
      }

      //add memberId to this.following array if it isn't in there, and then updateMyUser
      if(this.myUser.following.some(id => {
        const idStr = typeof id === 'object' ? (id._id || id.id || id) : id
        
        return idStr === memberId || idStr?.toString() === memberId?.toString()
      })){
        return
      }

      this.myUser.following.push(memberId)

      // Serialize follow calls so each updateMyUser completes before the next starts,
      // preventing race conditions where server responses overwrite pending follows
      followQueue = followQueue.then(() => this.updateMyUser()).catch(() => {})

    },
    async unfollow(memberId) {
      console.log("UNFOLLOWING", memberId)

      // Spouse/secondary pairs are permanently connected (hard-locked server-side).
      // Guard here so no caller (network, profile, restaurant favorited-by list)
      // can optimistically drop the spouse and then hit the API's 400.
      if (this.mySpouseId && String(memberId) === this.mySpouseId) {
        console.warn("Blocked unfollow of linked spouse/secondary account")
        
        return
      }

      //find this.user with _id of memberId, then set its followedByMyUser = false;
      let user = this.users.find(({_id}) => _id === memberId)
      if (user) {
        user.followedByMyUser = false
        user.followedByMyUserPending = false
        user.followingMyUser = false
      }

      //remove memberId from this.following array and then updateMyUser
      let removeIndex = this.myUser.following.findIndex(item => {
        const idStr = typeof item === 'object' ? (item._id || item.id || item) : item

        return idStr === memberId || idStr?.toString() === memberId?.toString()
      })
      ~removeIndex && this.myUser.following.splice(removeIndex, 1)

      //remove from followersApproved
      let approvedIndex = this.myUser.followersApproved.map(item => item).indexOf(memberId)
      ~approvedIndex && this.myUser.followersApproved.splice(approvedIndex, 1)

      //remove any approval given to the follow request and remove their follow of me
      this.myUser.removeFollowerApproved = memberId
      this.myUser.removeFollower = memberId

      await this.updateMyUser()

    },
    async removeFollower(memberId) {
      console.log("removeFollower", this.myUser.removeFollower)

      // Spouse/secondary pairs are permanently connected (hard-locked server-side).
      if (this.mySpouseId && String(memberId) === this.mySpouseId) {
        console.warn("Blocked removeFollower of linked spouse/secondary account")
        
        return
      }

      let user = this.users.find(({_id}) => _id === memberId)
      user.followingMyUser = false

      let removeIndex = this.myUser.followersApproved.map(item => item).indexOf(memberId)
      ~removeIndex && this.myUser.followersApproved.splice(removeIndex, 1)


      this.myUser.removeFollower = memberId
      await this.updateMyUser()
    },

    // ── Atomic notification actions ──
    async removeNotification(notificationId) {
      try {
        const res = await axios.delete(`${baseUrl}me/notifications/${notificationId}`)
        this.myUser.notifications = res.data
        localStorage.setItem('userData', JSON.stringify(this.myUser))
      } catch (error) {
        console.error('removeNotification error', error)
        reportError("users.store.js:removeNotification", error.message, null, null, null, error)
      }
    },
    async markNotificationRead(notificationId) {
      try {
        const res = await axios.put(`${baseUrl}me/notifications/${notificationId}/read`)
        this.myUser.notifications = res.data
        localStorage.setItem('userData', JSON.stringify(this.myUser))
      } catch (error) {
        console.error('markNotificationRead error', error)
        reportError("users.store.js:markNotificationRead", error.message, null, null, null, error)
      }
    },
    async markNotificationUnread(notificationId) {
      try {
        const res = await axios.put(`${baseUrl}me/notifications/${notificationId}/unread`)
        this.myUser.notifications = res.data
        localStorage.setItem('userData', JSON.stringify(this.myUser))
      } catch (error) {
        console.error('markNotificationUnread error', error)
        reportError("users.store.js:markNotificationUnread", error.message, null, null, null, error)
      }
    },
    async markAllNotificationsRead() {
      try {
        const res = await axios.put(`${baseUrl}me/notifications/mark-all-read`)
        this.myUser.notifications = res.data
        localStorage.setItem('userData', JSON.stringify(this.myUser))
      } catch (error) {
        console.error('markAllNotificationsRead error', error)
        reportError("users.store.js:markAllNotificationsRead", error.message, null, null, null, error)
      }
    },

    async approveFollower(memberId) {
      console.log("APPROVING", memberId)

      let user = this.users.find(({_id}) => _id === memberId)
      user.followingMyUser = true

      //adds memberId to this.myUser.followersApproved avoiding duplicates
      if(this.myUser.followersApproved.find(({id}) => id === memberId)){
        return
      }
      this.myUser.followersApproved.push(memberId)

      // auto follow back
      if(!this.myUser.following.find(({id}) => id === memberId)){
        user.followedByMyUser = true
        this.myUser.following.push(memberId)
      }

      await this.updateMyUser()
    }

  }
})
